'use client';

import { useState } from "react";
import Link from "next/link";
import { Button, Card, CardBody, CardHeader, Divider } from "@heroui/react";
import { TopicCluster } from "@/libs/topics";

const formatDateTime = (value: Date | null) => value
  ? new Intl.DateTimeFormat("uk-UA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value)
  : "Час невідомий";

const COLLAPSED_TIMELINE_ITEMS = 5;

export const StoryTopicPanel = ({ cluster }: { cluster: TopicCluster }) => {
  const [showFullTimeline, setShowFullTimeline] = useState(false);
  const visibleTimelineItems = showFullTimeline
    ? cluster.timeline
    : cluster.timeline.slice(0, COLLAPSED_TIMELINE_ITEMS);

  return (
    <Card shadow="sm" isBlurred className="border-none bg-background/60 dark:bg-default-100/50">
      <CardHeader className="flex flex-col items-start gap-2">
        <div className="text-sm text-default-500">Тема</div>
        <Link href={`/topic/${cluster.topicId}`} className="text-xl font-semibold hover:underline">
          {cluster.title}
        </Link>
        <p className="text-sm text-default-500">
          {cluster.sourceCount} джерел • {cluster.articles.length} матеріалів
          {cluster.lastUpdated ? ` • оновлено ${formatDateTime(cluster.lastUpdated)}` : ""}
        </p>
      </CardHeader>
      <Divider />
      <CardBody className="gap-6">
        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">Останнє по темі</h2>
          <div className="flex flex-col gap-3">
            {cluster.latestArticles.slice(0, 5).map((item) => (
              <div key={item.id} className="flex flex-col gap-1">
                <Link href={`/articles/${item.id}`} className="font-medium hover:underline">
                  {item.title || "Без заголовка"}
                </Link>
                <p className="text-sm text-default-500">
                  {item.sources?.name || "Невідоме джерело"} • {formatDateTime(item.pubdate ? new Date(item.pubdate) : null)}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">Таймлайн</h2>
          <div className="flex flex-col gap-3 border-l border-default-200 pl-4">
            {visibleTimelineItems.map((item) => (
              <div key={item.articleId} className="relative flex flex-col gap-1">
                <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-primary" />
                <p className="text-sm text-default-500">{formatDateTime(item.pubdate)}</p>
                <Link href={`/articles/${item.articleId}`} className="font-medium hover:underline">
                  {item.title}
                </Link>
                <p className="text-sm text-default-500">{item.sourceName}</p>
              </div>
            ))}
          </div>
          {cluster.timeline.length > COLLAPSED_TIMELINE_ITEMS ? (
            <div className="flex justify-start">
              <Button
                variant="light"
                size="sm"
                onPress={() => setShowFullTimeline((value) => !value)}
              >
                {showFullTimeline ? "Показати менше" : "Показати ще"}
              </Button>
            </div>
          ) : null}
        </section>

        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">Порівняння джерел</h2>
          <div className="flex flex-col gap-3">
            {cluster.sourceComparison.map((item) => (
              <div key={item.sourceId || item.articleId} className="rounded-lg border border-default-200 p-3">
                <p className="text-sm text-default-500">{item.sourceName}</p>
                <Link href={`/articles/${item.articleId}`} className="font-medium hover:underline">
                  {item.title}
                </Link>
                <p className="text-sm text-default-500">{formatDateTime(item.pubdate)}</p>
              </div>
            ))}
          </div>
        </section>
      </CardBody>
    </Card>
  );
};
